import React, { useState } from 'react';
import { Sparkles, Copy, Mail } from 'lucide-react';
import { Card, Input, Button, Badge } from '../ui';
import { cn } from '../../lib/utils';

export function HQPitchGenerator() {
  const [chapterName, setChapterName] = useState('Local Chapter 101');
  const [hqName, setHqName] = useState('National Headquarters');
  const [orgType, setOrgType] = useState('Association');
  const [painPoint, setPainPoint] = useState('Dues Collection');
  const [userName, setUserName] = useState('Jane Doe');
  const [copyStatus, setCopyStatus] = useState(false);

  const getPitchContent = () => {
    let painPointParagraph = '';
    
    if (painPoint === 'Dues Collection') {
      painPointParagraph = `Currently, our process for collecting dues and managing ledgers is highly fragmented. We spend hours tracking down payments and managing spreadsheets, which takes away from our core mission. OrgSets solves this by offering an automated ledger with direct Stripe integration and transparent dues-splitting, ensuring ${hqName} gets its share instantly without manual reconciliation.`;
    } else if (painPoint === 'Roster Management') {
      painPointParagraph = `Currently, we struggle with keeping an accurate, up-to-date roster of our members. Spreadsheets get out of sync, and tracking member status is a constant challenge. OrgSets solves this by providing a unified, multi-tier directory where members can manage their own profiles, ensuring ${hqName} always has real-time visibility into local chapter health and numbers.`;
    } else if (painPoint === 'Compliance/Waivers') {
      painPointParagraph = `Currently, collecting and securely storing signed waivers and compliance documents is a logistical nightmare. OrgSets solves this with its secure Digital Vault, allowing us to enforce mandatory document signing before members can participate. This reduces liability for both our chapter and ${hqName}.`;
    } else {
      painPointParagraph = `Currently, our communication and member engagement tools are scattered across different platforms, leading to low engagement and confusion. OrgSets solves this with its built-in Pulse Engine, allowing us to easily send newsletters and SMS alerts, keeping our members informed and aligned with ${hqName}'s initiatives.`;
    }

    return `Subject: Proposal: Upgrading our ${orgType} Management with OrgSets

Dear ${hqName} Leadership,

I'm writing on behalf of ${chapterName} to propose that we evaluate a new management platform called OrgSets (orgsets.com) for our organization.

${painPointParagraph}

OrgSets is specifically designed for multi-tiered organizations like ours. It supports hierarchical organization structures, meaning local chapters like ours can operate independently while seamlessly rolling up data, revenue, and compliance metrics to the national level. 

Furthermore, local chapters can start for free, and they offer an Enterprise tier that provides ${hqName} with unlimited hierarchical depth, multi-tier grant routing, and a dedicated network manager.

I believe adopting OrgSets could significantly streamline our operations from the grassroots up. I'd love to schedule a brief call to discuss this further or arrange a demo with their team.

Best regards,

${userName}
${chapterName}`;
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(getPitchContent());
    setCopyStatus(true);
    setTimeout(() => setCopyStatus(false), 2000);
  };

  return (
    <div className="grid md:grid-cols-2 gap-8">
      <Card className="p-6 space-y-4 border border-gray-150">
        <h3 className="text-lg font-bold flex items-center gap-2 mb-2">
          <Sparkles className="text-brand-primary" size={20} /> Pitch Details
        </h3>
        <div className="space-y-4">
          <div>
            <label className="text-[10px] font-bold text-gray-400 uppercase font-mono">Your Chapter/Club Name</label>
            <Input value={chapterName} onChange={(e) => setChapterName(e.target.value)} />
          </div>
          <div>
            <label className="text-[10px] font-bold text-gray-400 uppercase font-mono">Headquarters/National Name</label>
            <Input value={hqName} onChange={(e) => setHqName(e.target.value)} />
          </div>
          <div>
            <label className="text-[10px] font-bold text-gray-400 uppercase font-mono">Organization Type</label>
            <select
              value={orgType}
              onChange={(e) => setOrgType(e.target.value)}
              className="w-full h-[40px] px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-primary bg-white dark:bg-slate-900 dark:border-slate-800"
            >
              <option value="Association">Association / Non-Profit</option>
              <option value="Sports League">Sports League</option>
              <option value="Fraternity/Sorority">Fraternity / Sorority</option>
              <option value="Club">Club / Society</option>
              <option value="Union">Union / Guild</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] font-bold text-gray-400 uppercase font-mono">Primary Pain Point</label>
            <select
              value={painPoint}
              onChange={(e) => setPainPoint(e.target.value)}
              className="w-full h-[40px] px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-primary bg-white dark:bg-slate-900 dark:border-slate-800"
            >
              <option value="Dues Collection">Dues Collection & Financial Splits</option>
              <option value="Roster Management">Roster & Directory Management</option>
              <option value="Compliance/Waivers">Compliance & Liability Waivers</option>
              <option value="Communication">Member Communication & Alerts</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] font-bold text-gray-400 uppercase font-mono">Your Name / Title</label>
            <Input value={userName} onChange={(e) => setUserName(e.target.value)} />
          </div>
        </div>
      </Card>

      <Card className="p-6 bg-slate-50 dark:bg-slate-900 border border-gray-200 flex flex-col justify-between">
        <div className="space-y-3 flex-grow flex flex-col">
          <div className="flex justify-between items-center">
            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider font-mono">Generated Pitch Email</span>
            <Button size="sm" variant="secondary" onClick={handleCopy} className="gap-1 px-3 cursor-pointer">
              <Copy size={14} /> {copyStatus ? 'Copied!' : 'Copy Text'}
            </Button>
          </div>
          <div className="bg-white dark:bg-slate-950 border border-gray-150 dark:border-slate-800 p-6 rounded-xl shadow-sm flex-grow">
            <pre className="font-sans text-sm whitespace-pre-wrap leading-relaxed text-gray-700 dark:text-gray-300">
              {getPitchContent()}
            </pre>
          </div>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200/60 dark:border-slate-800 flex items-center justify-between">
          <p className="text-xs text-gray-500">
            Send this directly to your regional director or national executive board.
          </p>
          <Badge variant="amber" className="text-[10px] uppercase font-bold tracking-wider">Draft Template</Badge>
        </div>
      </Card>
    </div>
  );
}
